import { LogManager } from '@src/utility/Logger';

import type { StoryRepository } from '@src/repositories/StoryRepository';
import type { VideoRepository } from '@src/repositories/VideoRepository';
import type { ClipMetadata, VideoAction } from '@src/types.ts/story';

const logger = LogManager.getLogger('service:WriteQueue');

// --- Types ---

export type QueuedClipStatus = 'pending' | 'saving' | 'saved' | 'failed';

export interface LightweightVideoDTO {
  title: string;
  aspectRatio: string;
  sourcePlatform: string;
  clipJson: ClipMetadata;
  lastActionJson: VideoAction;
  isClientReady: boolean;
  transcriptionText?: string;
}

export interface QueuedClipSave {
  id: string; // Temp ID for tracking
  video: LightweightVideoDTO;
  storyId: string;
  status: QueuedClipStatus;
  retryCount: number;
  error?: string;
  savedVideoId?: string; // Real video ID after save completes
  savedSlug?: string; // Real slug after save completes
  savedHash?: string; // Real hash after save completes
  // Title renamed while the item was mid-save; written once the real ID lands
  pendingTitle?: string;
  onComplete?: (realId: string, slug: string, hash: string) => void;
  onError?: (error: Error) => void;
}

export interface QueueStatus {
  pending: number;
  saving: number;
  saved: number;
  failed: number;
  items: QueuedClipSave[];
  isProcessing: boolean;
}

export type StatusChangeCallback = (status: QueueStatus) => void;

export type PatchQueuedTitleResult =
  | { status: 'queued' }
  | { status: 'saved'; realId: string }
  | { status: 'failed'; error: string }
  | { status: 'missing' };

export interface WriteQueueConfig {
  maxRetries: number;
  retryBackoffMs: number[];
  persistToLocalStorage: boolean;
  localStorageKey: string;
  storyUpdateBatchSize: number; // Batch story updates every N clips
}

const DEFAULT_CONFIG: WriteQueueConfig = {
  maxRetries: 3,
  retryBackoffMs: [1000, 2000, 4000],
  persistToLocalStorage: true,
  localStorageKey: 'clip-save-queue',
  storyUpdateBatchSize: 3,
};

// --- Service Implementation ---

export class WriteQueueService {
  private queue: QueuedClipSave[] = [];
  private isProcessing = false;
  private subscribers: Set<StatusChangeCallback> = new Set();
  private config: WriteQueueConfig;
  private videoRepository: VideoRepository | null = null;
  private storyRepository: StoryRepository | null = null;
  private pendingStoryUpdates: Map<string, string[]> = new Map(); // storyId -> videoIds to add
  private beforeUnloadHandler: (() => void) | null = null;

  constructor(config: Partial<WriteQueueConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.loadFromLocalStorage();
    this.setupBeforeUnloadHandler();
  }

  // --- Initialization ---

  setRepositories(
    videoRepository: VideoRepository,
    storyRepository: StoryRepository,
  ): void {
    this.videoRepository = videoRepository;
    this.storyRepository = storyRepository;

    // Resume processing if we have pending items from a previous session
    if (this.queue.some(item => item.status === 'pending')) {
      logger.info(
        'WriteQueueService: resuming pending items from previous session',
        {
          pendingCount: this.queue.filter(i => i.status === 'pending').length,
        },
      );
      this.processQueue();
    }
  }

  // --- Public API ---

  /**
   * Queue items for background save
   */
  enqueue(items: Omit<QueuedClipSave, 'status' | 'retryCount'>[]): void {
    const newItems: QueuedClipSave[] = items.map(item => ({
      ...item,
      status: 'pending' as const,
      retryCount: 0,
    }));

    this.queue.push(...newItems);
    this.persistToLocalStorage();
    this.notifySubscribers();

    logger.debug('WriteQueueService: enqueued items', {
      count: newItems.length,
      tempIds: newItems.map(i => i.id),
    });

    this.processQueue();
  }

  /**
   * Get current queue status
   */
  getStatus(): QueueStatus {
    return {
      pending: this.queue.filter(i => i.status === 'pending').length,
      saving: this.queue.filter(i => i.status === 'saving').length,
      saved: this.queue.filter(i => i.status === 'saved').length,
      failed: this.queue.filter(i => i.status === 'failed').length,
      items: [...this.queue],
      isProcessing: this.isProcessing,
    };
  }

  /**
   * Subscribe to status changes
   */
  onStatusChange(callback: StatusChangeCallback): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Check if a clip is queued (by temp ID)
   */
  isQueued(tempId: string): boolean {
    return this.queue.some(
      item =>
        item.id === tempId &&
        (item.status === 'pending' || item.status === 'saving'),
    );
  }

  /**
   * Wait for a specific item to be saved
   */
  async waitForItem(
    tempId: string,
  ): Promise<{ realId: string } | { error: string }> {
    const item = this.queue.find(i => i.id === tempId);
    if (!item) {
      return { error: 'Item not found in queue' };
    }

    if (item.status === 'saved' && item.savedVideoId) {
      return { realId: item.savedVideoId };
    }

    if (item.status === 'failed') {
      return { error: item.error || 'Save failed' };
    }

    // Wait for the item to complete
    return new Promise(resolve => {
      const unsubscribe = this.onStatusChange(status => {
        const updatedItem = status.items.find(i => i.id === tempId);
        if (!updatedItem) {
          unsubscribe();
          resolve({ error: 'Item removed from queue' });
          return;
        }

        if (updatedItem.status === 'saved' && updatedItem.savedVideoId) {
          unsubscribe();
          resolve({ realId: updatedItem.savedVideoId });
        } else if (updatedItem.status === 'failed') {
          unsubscribe();
          resolve({ error: updatedItem.error || 'Save failed' });
        }
      });
    });
  }

  /**
   * Rename a clip that is still in the queue, without waiting for it.
   *
   * A pending item takes the title into its create payload, so the record is
   * born with the new name. An item already mid-save keeps the title aside and
   * writes it the moment the real ID lands (the create's payload was already
   * serialised). A saved item hands back its real ID for the caller to update
   * directly. Nothing here blocks or times out (PLAT-11777).
   */
  patchQueuedVideoTitle(tempId: string, title: string): PatchQueuedTitleResult {
    const item = this.queue.find(i => i.id === tempId);
    if (!item) return { status: 'missing' };

    switch (item.status) {
      case 'saved':
        return item.savedVideoId
          ? { status: 'saved', realId: item.savedVideoId }
          : { status: 'missing' };
      case 'failed':
        return { status: 'failed', error: item.error || 'Save failed' };
      case 'saving':
        // Also merged below so a reload (saving -> pending) recreates with it
        item.pendingTitle = title;
        break;
      case 'pending':
        break;
    }

    item.video = { ...item.video, title };
    this.persistToLocalStorage();
    logger.debug('WriteQueueService: patched queued title', {
      tempId,
      status: item.status,
    });
    return { status: 'queued' };
  }

  /**
   * Retry all failed items
   */
  retryFailed(): void {
    const failedItems = this.queue.filter(i => i.status === 'failed');
    if (failedItems.length === 0) return;

    for (const item of failedItems) {
      item.status = 'pending';
      item.retryCount = 0;
      item.error = undefined;
    }

    this.persistToLocalStorage();
    this.notifySubscribers();
    this.processQueue();

    logger.info('WriteQueueService: retrying failed items', {
      count: failedItems.length,
    });
  }

  /**
   * Remove failed items from the queue
   */
  discardFailed(): void {
    const failedCount = this.queue.filter(i => i.status === 'failed').length;
    this.queue = this.queue.filter(i => i.status !== 'failed');
    this.persistToLocalStorage();
    this.notifySubscribers();

    logger.info('WriteQueueService: discarded failed items', {
      count: failedCount,
    });
  }

  /**
   * Clear completed items from the queue
   */
  clearCompleted(): void {
    this.queue = this.queue.filter(i => i.status !== 'saved');
    this.persistToLocalStorage();
    this.notifySubscribers();
  }

  /**
   * Force process queue (e.g., on beforeunload)
   * Returns a promise that resolves when all pending items are saved
   */
  async flush(): Promise<void> {
    if (this.queue.every(i => i.status === 'saved' || i.status === 'failed')) {
      return;
    }

    logger.info('WriteQueueService: flushing queue');

    // Process all remaining items synchronously
    await this.processQueueSync();
  }

  /**
   * Cleanup resources when the service is destroyed
   */
  destroy(): void {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    this.subscribers.clear();
  }

  // --- Private Methods ---

  private async processQueue(): Promise<void> {
    if (this.isProcessing) return;
    if (!this.videoRepository || !this.storyRepository) {
      logger.debug(
        'WriteQueueService: repositories not set, skipping processing',
      );
      return;
    }

    const pendingItems = this.queue.filter(i => i.status === 'pending');
    if (pendingItems.length === 0) return;

    this.isProcessing = true;
    this.notifySubscribers();

    try {
      await this.processQueueInternal();
    } finally {
      this.isProcessing = false;
      this.notifySubscribers();
    }
  }

  private async processQueueInternal(): Promise<void> {
    let pendingItem = this.queue.find(i => i.status === 'pending');
    while (pendingItem) {
      await this.processSingleItem(pendingItem);
      pendingItem = this.queue.find(i => i.status === 'pending');
    }

    // Flush any remaining story updates
    await this.flushStoryUpdates();
  }

  private async processQueueSync(): Promise<void> {
    if (!this.videoRepository || !this.storyRepository) return;

    const pendingItems = this.queue.filter(
      i => i.status === 'pending' || i.status === 'saving',
    );

    for (const item of pendingItems) {
      if (item.status === 'pending') {
        await this.processSingleItem(item);
      }
    }

    await this.flushStoryUpdates();
  }

  private async processSingleItem(item: QueuedClipSave): Promise<void> {
    if (!this.videoRepository || !this.storyRepository) return;

    item.status = 'saving';
    this.persistToLocalStorage();
    this.notifySubscribers();

    try {
      const savedVideo = await this.videoRepository.saveLightweightVideo(
        item.video,
      );

      // A rename that arrived mid-save missed the create payload; land it
      // before anyone learns the real ID. Its failure must not re-run the
      // create, so it is caught here rather than in the catch below.
      if (item.pendingTitle !== undefined) {
        await this.applyPendingTitle(item, savedVideo.id);
      }

      item.status = 'saved';
      item.savedVideoId = savedVideo.id;
      item.savedSlug = savedVideo.slug;
      item.savedHash = savedVideo.hash;

      // Queue story update. Safe to await inside this try: flushStoryUpdates
      // handles its own failures and never rejects, so a story-link problem
      // cannot land in the catch below and re-run the clip's create.
      await this.queueStoryUpdate(item.storyId, savedVideo.id);

      // Call completion callback if provided
      if (item.onComplete) {
        try {
          item.onComplete(savedVideo.id, savedVideo.slug, savedVideo.hash);
        } catch (callbackError) {
          logger.error('WriteQueueService: onComplete callback error', {
            tempId: item.id,
            error: callbackError,
          });
        }
      }

      logger.debug('WriteQueueService: item saved', {
        tempId: item.id,
        realId: savedVideo.id,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (item.retryCount < this.config.maxRetries) {
        // Schedule retry
        const backoffMs = this.config.retryBackoffMs[item.retryCount] ?? 4000;
        item.retryCount++;
        item.status = 'pending';
        item.error = errorMessage;

        logger.warn('WriteQueueService: item failed, scheduling retry', {
          tempId: item.id,
          retryCount: item.retryCount,
          backoffMs,
          error: errorMessage,
        });

        // Wait for backoff before next attempt
        await new Promise(resolve => setTimeout(resolve, backoffMs));
      } else {
        // Max retries exceeded
        item.status = 'failed';
        item.error = errorMessage;

        // Call error callback if provided
        if (item.onError) {
          try {
            item.onError(
              error instanceof Error ? error : new Error(errorMessage),
            );
          } catch (callbackError) {
            logger.error('WriteQueueService: onError callback error', {
              tempId: item.id,
              error: callbackError,
            });
          }
        }

        logger.error('WriteQueueService: item failed after max retries', {
          tempId: item.id,
          retryCount: item.retryCount,
          error: errorMessage,
        });
      }
    }

    this.persistToLocalStorage();
    this.notifySubscribers();
  }

  private async applyPendingTitle(
    item: QueuedClipSave,
    realId: string,
  ): Promise<void> {
    // The item is still `saving` throughout this write, so a rename arriving
    // now is deferred here too rather than updating the record directly.
    // Drain, or the newest name is never written.
    while (item.pendingTitle !== undefined) {
      const title = item.pendingTitle;
      item.pendingTitle = undefined;
      if (!title || !this.videoRepository) return;
      try {
        await this.videoRepository.updateVideo({ id: realId, title });
      } catch (error) {
        logger.error('WriteQueueService: failed to apply pending title', {
          tempId: item.id,
          realId,
          error,
        });
        return;
      }
    }
  }

  private async queueStoryUpdate(
    storyId: string,
    videoId: string,
  ): Promise<void> {
    const existing = this.pendingStoryUpdates.get(storyId) ?? [];
    existing.push(videoId);
    this.pendingStoryUpdates.set(storyId, existing);

    // Flush if we've reached the batch size. Awaited so the next clip's batch
    // cannot start while this write is still open: unawaited, the clip that
    // completed mid-write pushed onto the array this flush was holding, tripped
    // the batch threshold a second time, and re-sent every ID in it — which is
    // what linked the first three clips of a four-clip save twice.
    if (existing.length >= this.config.storyUpdateBatchSize) {
      await this.flushStoryUpdates(storyId);
    }
  }

  private async flushStoryUpdates(specificStoryId?: string): Promise<void> {
    if (!this.storyRepository) return;

    const storiesToUpdate = specificStoryId
      ? ([
          [
            specificStoryId,
            this.pendingStoryUpdates.get(specificStoryId) ?? [],
          ],
        ] as const)
      : Array.from(this.pendingStoryUpdates.entries());

    for (const [storyId, videoIds] of storiesToUpdate) {
      if (videoIds.length === 0) continue;

      // Claim the batch before the await, not after it. A clip completing while
      // this write is open must start a fresh batch rather than push onto the
      // array being written — otherwise the next flush re-sends these IDs on
      // top of the ones this write is about to store.
      this.pendingStoryUpdates.delete(storyId);

      try {
        await this.storyRepository.addMultipleVideosToStory(
          storyId,
          videoIds as string[],
        );
        logger.debug('WriteQueueService: added videos to story', {
          storyId,
          videoIds,
        });
      } catch (error) {
        logger.error('WriteQueueService: failed to add videos to story', {
          storyId,
          videoIds,
          error,
        });
      }
    }
  }

  private notifySubscribers(): void {
    const status = this.getStatus();
    for (const callback of this.subscribers) {
      try {
        callback(status);
      } catch (error) {
        logger.error('WriteQueueService: subscriber callback error', { error });
      }
    }
  }

  // --- Persistence ---

  private persistToLocalStorage(): void {
    if (!this.config.persistToLocalStorage) return;

    try {
      // Only persist pending and saving items (not completed/failed from old sessions)
      const itemsToPersist = this.queue.filter(
        i => i.status === 'pending' || i.status === 'saving',
      );
      localStorage.setItem(
        this.config.localStorageKey,
        JSON.stringify(itemsToPersist),
      );
    } catch (error) {
      logger.warn('WriteQueueService: failed to persist to localStorage', {
        error,
      });
    }
  }

  private loadFromLocalStorage(): void {
    if (!this.config.persistToLocalStorage) return;

    try {
      const stored = localStorage.getItem(this.config.localStorageKey);
      if (stored) {
        const items: QueuedClipSave[] = JSON.parse(stored);
        // Reset 'saving' items to 'pending' since we're starting fresh
        this.queue = items.map(item => ({
          ...item,
          status: item.status === 'saving' ? ('pending' as const) : item.status,
        }));

        if (this.queue.length > 0) {
          logger.info('WriteQueueService: loaded items from localStorage', {
            count: this.queue.length,
          });
        }
      }
    } catch (error) {
      logger.warn('WriteQueueService: failed to load from localStorage', {
        error,
      });
      this.queue = [];
    }
  }

  private setupBeforeUnloadHandler(): void {
    this.beforeUnloadHandler = () => {
      // Persist current state before page unload
      this.persistToLocalStorage();
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }
}

// --- Helper to generate and identify temp IDs ---

const TEMP_CLIP_PREFIX = 'temp-clip-';

export function generateTempClipId(): string {
  return `${TEMP_CLIP_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function isTempClipId(id: string | undefined): boolean {
  return id != null && id.startsWith(TEMP_CLIP_PREFIX);
}

// --- Singleton instance ---

export const writeQueueService = new WriteQueueService();
